/**
 * RegistrationRequests — the access-request queue (CC 0373, JAI-2780) on the
 * Staff list. Ported verbatim on 2026-10-16 (Pat) from the quarantined
 * screens/people/StaffAndRoles.tsx when `people` was removed; the only change is
 * that the owner gate is computed here instead of passed in.
 *
 * Data: bound — listTeamMembers(status=requested) · approveTeamMemberRequest ·
 * declineTeamMemberRequest (unchanged from the source).
 */
import { useEffect, useRef, useState } from "react";
import { Chip, ChipToggleGroup, IconTile, Avatar, Banner, Btn, Card, Checkbox, Icon, StatusPill, T, Textarea } from "@v0y4ge/jrni-ui";
import { listTeamMembers, approveTeamMemberRequest, declineTeamMemberRequest, isApiError } from "@v0y4ge/api-client";
import { toast } from "../../shell/toast";
import { can, useUser, refetchMembers, getConfirmedTenantSlug, getTenantGeneration, onTenantSwitching, onTenantChange, onSessionEnd, getSessionEpoch, onTenantIdentityResolved, listApiRoles, getApiRole, type ApiRole } from "../../shell/access";
import { friendlyDateTime } from "../../lib/datetime";
import { SkeletonBox } from "../../lib/loading-skeletons";
import type { TeamMember } from "@v0y4ge/api-contracts";
import { useT, formatICU, t as i18nT } from "../../lib/i18n";

/* ── Workspace roles (ported from Settings › Team & roles, retired 2026-08-10) ──
 * TWO role vocabularies meet on this screen, deliberately unmapped:
 *   • WORKSPACE roles — what the team API administers (owner/admin/member…).
 *     Invites and membership writes carry these.
 *   • PROCESS roles — shell/access.ts's capability model `can()` resolves
 *     against, recorded per person and per location and edited on the Roles tab.
 * No equivalence is invented between them. */
function WorkspaceRolePicker({ roles, value, onChange }: { roles: ApiRole[]; value: string; onChange: (key: string) => void }) {
  const t = useT();
  return (
    <ChipToggleGroup
      ariaLabel={t("people.staffAndRoles.ariaLabel", "Role")}
      value={value}
      onChange={(v) => onChange(v as string)}
      options={roles.map((r) => ({ value: r.key, label: r.label }))}
    />
  );
}

/* Capability labels (2026-10-08). The backend builds `permissions[i]` as the
   catalog LABEL KEY for `capabilities[i]` ("shell.access.viewAuditLog"); it was
   printed raw. Keys go through the i18n catalog, falling back to a label read
   off the capability id itself, never to the key. */
const LABEL_KEY = /^[A-Za-z][\w-]*(\.[\w-]+)+$/;
const ACCESS_VERB: Record<string, [string, string]> = {
  read: ["staffRoles.capVerbView", "View"], view: ["staffRoles.capVerbView", "View"], list: ["staffRoles.capVerbView", "View"],
  write: ["staffRoles.capVerbManage", "Manage"], manage: ["staffRoles.capVerbManage", "Manage"],
  edit: ["staffRoles.capVerbManage", "Manage"], update: ["staffRoles.capVerbManage", "Manage"],
};
const ACCESS_LEVEL: Record<string, [string, string]> = {
  read: ["staffRoles.capLevelRead", "read access"], view: ["staffRoles.capLevelRead", "read access"], list: ["staffRoles.capLevelRead", "read access"],
  write: ["staffRoles.capLevelWrite", "write access"], manage: ["staffRoles.capLevelWrite", "write access"],
  edit: ["staffRoles.capLevelWrite", "write access"], update: ["staffRoles.capLevelWrite", "write access"],
};
const words = (s: string) => s.replace(/([a-z])([A-Z])/g, "$1 $2").replace(/[_-]+/g, " ").trim().toLowerCase();
const capAction = (capId: string) => (capId.includes(".") ? capId.slice(capId.lastIndexOf(".") + 1) : "");
/** "audit.read" → "View audit"; "identity.team.manage" → "Manage identity team". */
function capabilityFallbackLabel(capId: string): string {
  const action = capAction(capId);
  const subject = words(action ? capId.slice(0, capId.lastIndexOf(".")).replace(/\./g, " ") : capId);
  const verb = ACCESS_VERB[action.toLowerCase()];
  const head = verb ? i18nT(verb[0], verb[1]) : words(action);
  const text = head ? `${head} ${subject}` : subject;
  return text.charAt(0).toUpperCase() + text.slice(1);
}
function capabilityLabel(capId: string, labelKey: string | undefined): string {
  const fallback = capabilityFallbackLabel(capId);
  if (!labelKey || labelKey === capId) return fallback;
  return LABEL_KEY.test(labelKey) ? i18nT(labelKey, fallback) : labelKey;
}
/** What distinguishes one capability from another that shares its label —
 *  `audit.read` and `audit.write` both carry viewAuditLog today. */
function capabilityLevel(capId: string): string {
  const action = capAction(capId);
  const level = ACCESS_LEVEL[action.toLowerCase()];
  return level ? i18nT(level[0], level[1]) : words(action) || capId;
}
/** The read-only list, one entry per distinct capability and per distinct
 *  visible label. Capabilities that resolve to the SAME label are not merged
 *  into one claim: each keeps its own entry, told apart by access level. */
function readOnlyCapabilityItems(role: ApiRole): { key: string; label: string }[] {
  const firstIndex = new Map<string, number>();
  role.capabilities.forEach((capId, i) => { if (!firstIndex.has(capId)) firstIndex.set(capId, i); });
  const byLabel = new Map<string, string[]>();
  firstIndex.forEach((i, capId) => {
    const label = capabilityLabel(capId, role.permissions[i]);
    byLabel.set(label, [...(byLabel.get(label) ?? []), capId]);
  });
  const items: { key: string; label: string }[] = [];
  const seen = new Set<string>();
  byLabel.forEach((ids, label) => {
    for (const capId of ids) {
      let text = ids.length > 1 ? `${label} (${capabilityLevel(capId)})` : label;
      if (seen.has(text)) text = `${label} (${capabilityFallbackLabel(capId)})`;
      seen.add(text);
      items.push({ key: capId, label: text });
    }
  });
  return items;
}

/** Capability rows for a WORKSPACE role. Editable only on invite, where the API
 *  accepts a NARROWED set; it rejects capabilities above the role. Read-only
 *  everywhere else, where it is a static list (2026-10-08): it used to be a
 *  two-column form of disabled, checked boxes, which reads as a control that
 *  is merely switched off. */
function ApiCapabilityList({ role, selected, onChange }: { role: ApiRole | undefined; selected?: Set<string>; onChange?: (next: Set<string>) => void }) {
  const t = useT();
  if (!role) return null;
  if (!onChange) {
    const items = readOnlyCapabilityItems(role);
    if (!items.length) {
      return <div style={{ fontSize: 12, color: T.soft }}>{t("staffRoles.noCapabilitiesInRole", "This role grants no capabilities.")}</div>;
    }
    return (
      <ul aria-label={t("staffRoles.capabilitiesInThisRole", "Capabilities in this role")} data-testid="role-capabilities-readonly"
        style={{ listStyle: "none", margin: 0, padding: 0, display: "flex", flexWrap: "wrap", gap: 6 }}>
        {items.map((it) => (
          <li key={it.key} title={it.key} style={{ minWidth: 0, maxWidth: "100%" }}>
            <StatusPill tone="neutral" style={{ whiteSpace: "normal", overflowWrap: "anywhere", maxWidth: "100%" }}>{it.label}</StatusPill>
          </li>
        ))}
      </ul>
    );
  }
  return (
    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "6px 16px" }}>
      {[...new Set(role.capabilities)].map((capId) => {
        const i = role.capabilities.indexOf(capId);
        const checked = !!selected?.has(capId);
        return (
          <label key={capId} style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer", padding: "4px 0" }}>
            <Checkbox checked={checked} onChange={(on) => {
              const next = new Set(selected ?? []);
              if (on) next.add(capId); else next.delete(capId);
              onChange(next);
            }} />
            <span style={{ fontSize: 12, color: checked ? T.n1 : T.soft, textDecoration: checked ? "none" : "line-through" }}>{capabilityLabel(capId, role.permissions[i])}</span>
          </label>
        );
      })}
    </div>
  );
}

/** Only the signed-in tenant Owner can grant or change workspace access (verified
 *  session role). Ported with the queue; the roster-failure input of the original
 *  is not available on this screen, so a failed queue read is what fails closed. */
function useCanManageWorkspaceRoles(): boolean {
  const user = useUser();
  return user.workspaceRole?.trim().toLowerCase() === "owner";
}

/* ── Registration requests (CC 0373, JAI-2780) ──────────────────────────────
 * People who signed up, or asked for access from their account, wait here for an admin. Shown only to
 * people who can manage the team (the Invite gate); everyone else sees no section at all. The queue is
 * read with status=requested, which the ordinary team list never includes, and its total is the
 * server's pagination.total. Approve grants a workspace role exactly as an invitation does (the same
 * picker and narrowing, the same owner rule); Decline grants nothing and the requester is not told.
 * One Idempotency-Key per user action, kept across a retry, so a double click replays the first answer. */
const TEAM_OPTS = { baseUrl: (window as unknown as { __STUDIO_API_BASE__?: string }).__STUDIO_API_BASE__ ?? "" } as never;
const newKey = () => (typeof crypto !== "undefined" && "randomUUID" in crypto ? crypto.randomUUID() : String(Date.now()) + Math.random().toString(16).slice(2));
function requestError(e: unknown): string {
  const code = isApiError(e) ? e.code : "";
  if (code === "TEAM_MEMBER_ROLE_INVALID" || code === "TEAM_MEMBER_ROLE_FORBIDDEN") return i18nT("staffRoles.requestRoleNotAllowed", "You can't grant that role. Choose another, or ask the workspace owner.");
  if (code === "TEAM_MEMBER_NOT_FOUND") return i18nT("staffRoles.requestGone", "This request was already decided or has expired. The list has been refreshed.");
  if (code === "TEAM_MEMBER_REQUEST_CONFLICT") return i18nT("staffRoles.requestDecidedElsewhere", "Someone else has already decided this request. The list has been refreshed.");
  return e instanceof Error ? e.message : String(e);
}
function expiresLabel(iso: string | null): string {
  if (!iso) return "";
  const ms = Date.parse(iso) - Date.now();
  if (Number.isNaN(ms)) return "";
  const days = Math.ceil(ms / 86_400_000);
  if (days <= 1) return i18nT("staffRoles.requestExpiresToday", "Expires within a day");
  return formatICU(i18nT("staffRoles.requestExpiresIn.icu", "Expires in {n} days"), { n: days });
}

function RequestRow({ m, canManageWorkspaceRoles, onDecided }: { m: TeamMember; canManageWorkspaceRoles: boolean; onDecided: (stale?: boolean) => void }) {
  const t = useT();
  const [mode, setMode] = useState<null | "approve" | "decline">(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const apiRoles = listApiRoles();
  const [roleKey, setRoleKey] = useState("");
  const wsRole = getApiRole(canManageWorkspaceRoles ? (roleKey || "member") : "member");
  const [narrowed, setNarrowed] = useState(false);
  const [caps, setCaps] = useState<Set<string>>(new Set());
  const [reason, setReason] = useState("");
  const keyRef = useRef<string>("");
  const open = (next: "approve" | "decline") => { keyRef.current = newKey(); setErr(null); setMode(next); };
  const name = m.displayName || m.email;
  const mountedRef = useRef(true);
  /* Set in the effect body, not only cleared: StrictMode mounts, unmounts and remounts (CC 0386). */
  useEffect(() => { mountedRef.current = true; return () => { mountedRef.current = false; }; }, []);
  /* A decision belongs to the workspace it was made in (CC 0383): the workspace and generation are captured
     when the action starts, and an answer that lands after a switch does nothing visible: no toast naming
     the applicant, no message, no refresh, no retry state. The same holds once the row has unmounted (the
     admin left the screen; CC 0385), which no scope check can see. The server's decision itself stands. */
  const run = async (fn: () => Promise<unknown>, done: string) => {
    if (busy) return;
    const scope = requestsScope();
    if (!scope) return;
    const same = () => mountedRef.current && scope === requestsScope();
    setBusy(true); setErr(null);
    try {
      await fn();
      if (!same()) return;
      toast({ tone: "success", title: done, subtitle: name });
      onDecided();
    } catch (e) {
      if (!same()) return;
      const code = isApiError(e) ? e.code : "";
      if (mountedRef.current) setErr(requestError(e));
      if (code === "TEAM_MEMBER_NOT_FOUND" || code === "TEAM_MEMBER_REQUEST_CONFLICT") onDecided(true);
    } finally { if (mountedRef.current) setBusy(false); }
  };
  const approve = () => {
    if (!wsRole) { setErr(t("staffRoles.workspaceRoleUnavailable", "Workspace roles are still loading. Please retry.")); return; }
    void run(() => approveTeamMemberRequest(m.id, { role: wsRole.key, ...(canManageWorkspaceRoles && narrowed ? { capabilities: [...caps] } : {}) }, { idempotencyKey: keyRef.current } as never), t("staffRoles.requestApproved", "Request approved"));
  };
  const decline = () => {
    const r = reason.trim();
    void run(() => declineTeamMemberRequest(m.id, r ? { reason: r.slice(0, 500) } : {}, { idempotencyKey: keyRef.current } as never), t("staffRoles.requestDeclined", "Request declined"));
  };
  const requested = m.requestedAt ? friendlyDateTime(m.requestedAt) : "";
  return (
    <div data-testid="team-request-row" style={{ padding: "14px 16px", borderTop: `1px solid ${T.border}`, display: "flex", flexDirection: "column", gap: 10 }}>
      <div style={{ display: "flex", gap: 12, alignItems: "flex-start", flexWrap: "wrap" }}>
        <Avatar size={32} initials={(name || "?").slice(0, 2).toUpperCase()} />
        <div style={{ flex: "1 1 220px", minWidth: 0 }}>
          <div style={{ fontWeight: 600, color: T.n1, overflowWrap: "anywhere" }}>{m.displayName || m.email}</div>
          {m.displayName ? <div style={{ fontSize: 12, color: T.soft, overflowWrap: "anywhere" }}>{m.email}</div> : null}
          <div style={{ fontSize: 12, color: T.soft, marginTop: 2, display: "flex", gap: 8, flexWrap: "wrap" }}>
            {requested ? <span>{formatICU(t("staffRoles.requestedOn.icu", "Requested {when}"), { when: requested })}</span> : null}
            {m.requestExpiresAt ? <span>· {expiresLabel(m.requestExpiresAt)}</span> : null}
          </div>
          {m.requestMessage ? <div style={{ marginTop: 8, padding: "8px 10px", background: T.n6, borderRadius: 8, fontSize: 13, color: T.n1, whiteSpace: "pre-wrap", overflowWrap: "anywhere" }}>{m.requestMessage}</div> : null}
        </div>
        {mode === null ? (
          <div style={{ display: "flex", gap: 8 }}>
            <Btn size="sm" variant="secondary" onClick={() => open("decline")} testid="team-request-decline">{t("staffRoles.decline", "Decline")}</Btn>
            <Btn size="sm" icon={<Icon.check size={14} />} onClick={() => open("approve")} testid="team-request-approve">{t("staffRoles.approve", "Approve")}</Btn>
          </div>
        ) : null}
      </div>
      {mode === "approve" ? (
        <div style={{ padding: "12px 16px", background: T.n6, borderRadius: 8, display: "flex", flexDirection: "column", gap: 10 }}>
          <div style={{ fontSize: 12, fontWeight: 600, color: T.n2 }}>{t("staffRoles.workspaceRole", "Workspace role")}</div>
          {apiRoles.length && wsRole ? (
            canManageWorkspaceRoles ? <WorkspaceRolePicker roles={apiRoles} value={wsRole.key} onChange={setRoleKey} /> : <div><Chip selected effect="inert">{wsRole.label}</Chip></div>
          ) : <div style={{ fontSize: 12, color: T.soft }}>{t("staffRoles.workspaceRoleUnavailable", "Workspace roles are still loading. Please retry.")}</div>}
          {wsRole?.description ? <div style={{ fontSize: 12, color: T.soft, lineHeight: 1.45 }}>{wsRole.description}</div> : null}
          {canManageWorkspaceRoles && wsRole ? <div><Btn size="sm" variant="link" icon={<Icon.chev size={12} dir={narrowed ? "up" : "down"} />}
            onClick={() => { const next = !narrowed; setNarrowed(next); if (next) setCaps(new Set(wsRole.capabilities)); }}>
            {narrowed ? t("staffRoles.hideCapabilities", "Hide capabilities") : t("staffRoles.narrowCapabilitiesRequest", "Narrow the capabilities they get")}
          </Btn></div> : null}
          {canManageWorkspaceRoles && narrowed && wsRole ? <ApiCapabilityList role={wsRole} selected={caps} onChange={setCaps} /> : null}
          {err ? <div role="alert" style={{ fontSize: 12, color: T.red1 }}>{err}</div> : null}
          <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
            <Btn size="sm" variant="secondary" disabled={busy} onClick={() => setMode(null)}>{t("common.actions.cancel", "Cancel")}</Btn>
            <Btn size="sm" disabled={busy || !wsRole} onClick={approve} testid="team-request-approve-confirm">{busy ? t("staffRoles.approving", "Approving…") : formatICU(t("staffRoles.approveAs.icu", "Approve as {role}"), { role: wsRole?.label ?? "" })}</Btn>
          </div>
        </div>
      ) : null}
      {mode === "decline" ? (
        <div style={{ padding: "12px 16px", background: T.n6, borderRadius: 8, display: "flex", flexDirection: "column", gap: 10 }}>
          <Textarea label={t("staffRoles.declineReason", "Reason (optional, for staff only)")} value={reason} maxLength={500} rows={2}
            onChange={(e) => setReason(e.target.value)}
            hint={t("staffRoles.declineReasonHint", "The person is not told they were declined and never sees this reason.")} />
          {err ? <div role="alert" style={{ fontSize: 12, color: T.red1 }}>{err}</div> : null}
          <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
            <Btn size="sm" variant="secondary" disabled={busy} onClick={() => setMode(null)}>{t("common.actions.cancel", "Cancel")}</Btn>
            <Btn size="sm" variant="danger" disabled={busy} onClick={decline} testid="team-request-decline-confirm">{busy ? t("staffRoles.declining", "Declining…") : t("staffRoles.declineRequest", "Decline request")}</Btn>
          </div>
        </div>
      ) : null}
    </div>
  );
}

/* The queue is read page by page (CC 0380): "Load more" appends the next page, and the count is always the
   server's pagination.total. After a decision every page already loaded is read again from page 1, so the
   next request moves up and none is skipped. */
export const REQUESTS_PAGE = 50;
/* Applicants are not members of any workspace yet, so the section is scoped to one (workspace, tenant
   generation, session epoch) (CC 0382, 0383, 0384). The one rule, for every async path: anything started under
   a scope never changes the screen once that scope is gone.
     • first load, Load more, the refresh after a decision: tagged with the scope and a read number; an answer
       for another scope or an older read is dropped. If the scope moved with no signal reaching us, the
       answer is dropped and the CURRENT scope is read, so nothing waits on a read that will never land.
     • approve / decline (RequestRow.run): the scope is captured at the start; a late success or failure
       shows nothing (no toast, message, refresh or roster re-read).
     • the team-roster re-read (refetchMembers) runs only from a decision that passed that check.
     • unmount: every subscription is removed and any answer still in flight is dropped.
     • sign-out / sign-in / switch: onTenantSwitching and onSessionEnd empty the section at once;
       onTenantChange and onTenantIdentityResolved (first confirmation) read the new scope.
   Subscriptions are taken BEFORE the first read, and the confirmed workspace is checked again after, so a
   confirmation landing between render and subscribe is never missed. With no workspace confirmed the
   section is a quiet empty state, never "loading": loading is only ever set when a read has started. */
const requestsScope = (): string | null => { const slug = getConfirmedTenantSlug(); return slug ? slug + "#" + getTenantGeneration() + "#" + getSessionEpoch() : null; };
type RequestsState = { rows: TeamMember[]; total: number; pages: number; hasMore: boolean; loading: boolean; more: boolean; error: string | null; noWorkspace: boolean };
const NO_WORKSPACE: RequestsState = { rows: [], total: 0, pages: 0, hasMore: false, loading: false, more: false, error: null, noWorkspace: true };
const STARTING: RequestsState = { ...NO_WORKSPACE, loading: true, noWorkspace: false };
export function RegistrationRequests({ onApproved }: { onApproved?: () => void }) {
  const t = useT();
  const canManageWorkspaceRoles = useCanManageWorkspaceRoles();
  const [state, setState] = useState<RequestsState>(() => (requestsScope() ? STARTING : NO_WORKSPACE));
  const genRef = useRef(0);
  const scopeRef = useRef<string | null>(null);
  const mountedRef = useRef(true);
  /* The latest read, for the scope it was started under, and that scope still current. */
  const isLive = (gen: number, scope: string) => mountedRef.current && gen === genRef.current && scope === scopeRef.current && scope === requestsScope();
  /* A read whose own scope moved under it, with no newer read started: read the current scope instead. */
  const orphaned = (gen: number) => mountedRef.current && gen === genRef.current;
  const readPages = async (count: number, scope: string) => {
    const rows: TeamMember[] = []; let total = 0; let hasMore = false; let pages = 0;
    for (let page = 1; page <= Math.max(1, count); page += 1) {
      if (page > 1 && requestsScope() !== scope) throw new Error("superseded");
      const r = await listTeamMembers({ status: "requested", page, limit: REQUESTS_PAGE }, TEAM_OPTS);
      const seen = new Set(rows.map((m) => m.id));
      rows.push(...r.members.filter((m) => m.status === "requested" && !seen.has(m.id)));
      total = r.pagination?.total ?? rows.length; hasMore = !!r.pagination?.hasMore; pages = page;
      if (!hasMore) break;
    }
    return { rows, total, hasMore, pages };
  };
  const load = (count: number) => {
    if (!mountedRef.current) return;
    const gen = ++genRef.current;
    const scope = requestsScope();
    scopeRef.current = scope;
    if (!scope) { setState(NO_WORKSPACE); return; } // nothing is read, and nothing waits
    setState((s) => (s.noWorkspace ? STARTING : { ...s, loading: true, error: null }));
    readPages(count, scope)
      .then((r) => { if (isLive(gen, scope)) setState({ ...r, loading: false, more: false, error: null, noWorkspace: false }); else if (orphaned(gen)) load(1); })
      .catch((e) => { if (isLive(gen, scope)) setState((s) => ({ ...s, loading: false, more: false, error: e instanceof Error ? e.message : String(e) })); else if (orphaned(gen)) load(1); });
  };
  /* A switch or session end empties the section at once; nothing of the old scope stays on screen. */
  const clear = () => { genRef.current += 1; scopeRef.current = null; if (mountedRef.current) setState(NO_WORKSPACE); };
  useEffect(() => {
    mountedRef.current = true;
    const reread = () => { clear(); load(1); };
    const offs = [onTenantSwitching(clear), onSessionEnd(clear), onTenantChange(reread), onTenantIdentityResolved(reread)];
    load(1); // after subscribing: a confirmation that landed before this line is read here, one after it by `reread`
    return () => { mountedRef.current = false; genRef.current += 1; offs.forEach((off) => off()); };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const loadMore = () => {
    if (state.more || !state.hasMore) return;
    const gen = genRef.current; const next = state.pages + 1; const scope = scopeRef.current;
    if (!scope || !isLive(gen, scope)) { load(Math.max(1, state.pages)); return; }
    setState((s) => ({ ...s, more: true, error: null }));
    listTeamMembers({ status: "requested", page: next, limit: REQUESTS_PAGE }, TEAM_OPTS)
      .then((r) => {
        if (!isLive(gen, scope)) { if (orphaned(gen)) load(1); return; }
        setState((s) => {
          const seen = new Set(s.rows.map((m) => m.id));
          return { ...s, rows: [...s.rows, ...r.members.filter((m) => m.status === "requested" && !seen.has(m.id))], total: r.pagination?.total ?? s.total, hasMore: !!r.pagination?.hasMore, pages: next, more: false };
        });
      })
      .catch((e) => { if (isLive(gen, scope)) setState((s) => ({ ...s, more: false, error: e instanceof Error ? e.message : String(e) })); else if (orphaned(gen)) load(1); });
  };
  /* After a decision: reload the current workspace only, for as many pages as were shown. */
  const reload = () => load(Math.max(1, state.pages));
  const first = state.loading && state.rows.length === 0;
  return (
    /* 2026-10-16 redesign: one compact strip when nothing waits (icon · title ·
       count · status on the right), and the same header carrying a brand count
       badge above the rows when requests are pending — no tall empty card. */
    <Card style={{ padding: 0, overflow: "hidden", marginBottom: 16 }} testid="team-requests">
      <div style={{ padding: "14px 18px", display: "flex", alignItems: "center", gap: 14, flexWrap: "wrap", borderBottom: state.rows.length ? "1px solid " + T.n6 : "none" }}>
        <IconTile icon={<Icon.users size={16} />} />
        <div style={{ display: "flex", flexDirection: "column", gap: 2, minWidth: 0, flex: "1 1 220px" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <span style={{ fontWeight: 600, fontSize: 15, color: T.n1 }} data-testid="team-requests-title">{t("staffRoles.requests", "Requests")}</span>
            {first || state.noWorkspace ? null : (
              <span data-testid="team-requests-count" style={{
                minWidth: 22, height: 22, padding: "0 7px", borderRadius: 999, display: "inline-flex", alignItems: "center", justifyContent: "center",
                fontSize: 12, fontWeight: 600, fontVariantNumeric: "tabular-nums",
                background: state.total ? T.brandFill : T.n6, color: state.total ? T.onColor : T.n2,
              }}>{state.total}</span>
            )}
          </div>
          <span style={{ fontSize: 13, color: T.soft }}>{t("staffRoles.requestsSub", "People waiting for access to this workspace.")}</span>
        </div>
        {state.noWorkspace ? <span style={{ fontSize: 13, color: T.soft }} data-testid="team-requests-no-workspace">{t("staffRoles.requestsNoWorkspace", "Requests show once the workspace has loaded.")}</span>
          : first ? <div style={{ width: 160 }} data-testid="team-requests-loading" aria-busy="true"><SkeletonBox width="100%" height={20} /></div>
          : !state.error && state.rows.length === 0 ? (
            <span data-testid="team-requests-empty" style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 13, color: T.soft }}>
              <Icon.check size={14} />{t("staffRoles.noRequests", "No requests waiting.")}
            </span>
          ) : null}
      </div>
      {state.noWorkspace || first ? null
        : state.error && state.rows.length === 0 ? <div style={{ padding: "0 18px 16px" }}><Banner tone="danger" title={t("staffRoles.requestsFailed", "Couldn't load requests")} action={<Btn size="sm" variant="secondary" onClick={reload}>{t("common.actions.retry", "Retry")}</Btn>}>{state.error}</Banner></div>
        : state.rows.length === 0 ? null
        : <>
            {state.rows.map((m) => <RequestRow key={m.id} m={m} canManageWorkspaceRoles={canManageWorkspaceRoles}
              onDecided={(stale) => { reload(); if (!stale) { void refetchMembers(); onApproved?.(); } }} />)}
            {state.error ? <div style={{ padding: "8px 16px" }}><Banner tone="danger" size="compact" title={t("staffRoles.requestsMoreFailed", "Couldn't load more requests")} action={<Btn size="sm" variant="secondary" onClick={state.hasMore ? loadMore : reload}>{t("common.actions.retry", "Retry")}</Btn>}>{state.error}</Banner></div> : null}
            {state.hasMore ? (
              <div style={{ padding: "10px 16px 14px", borderTop: `1px solid ${T.border}`, display: "flex", alignItems: "center", gap: 12 }}>
                <span style={{ fontSize: 12, color: T.soft }}>{formatICU(t("staffRoles.requestsShowing.icu", "Showing {shown} of {total}"), { shown: state.rows.length, total: state.total })}</span>
                <Btn size="sm" variant="secondary" disabled={state.more || state.loading} onClick={loadMore} testid="team-requests-more">{state.more ? t("staffRoles.loadingMore", "Loading…") : t("staffRoles.loadMore", "Load more")}</Btn>
              </div>
            ) : null}
          </>}
    </Card>
  );
}

